/**
 * Harvard Citation Guide Content
 * This content is displayed in a popover when the citation guide info icon is clicked.
 */

export const harvardCitationGuide = {
  title: "Harvard Citation Guide",
  introduction: "Harvard is a commonly used method of referencing, which uses the Author-Date system.",
  
  sections: [
    {
      title: "Which Harvard style?",
      content: "Harvard has been adapted to suit many different publication styles. The style used in this guide follows the standard prescribed by the following manual. This is the official style followed in most Australian Government publications.\n\nSnooks & Co. 2002, Style manual for authors, editors and printers, 6th edn, John Wiley & Sons, Milton, Qld."
    },
    {
      title: "Why reference your sources?",
      content: "It is important to reference the sources you use for essays and reports, so that the reader can follow your arguments and check your sources. It is essential to correctly acknowledge the author when quoting or using other people's ideas in your work."
    },
    {
      title: "How do I use Harvard?",
      content: "Harvard uses in-text citations and reference lists at the end of your paper."
    },
    {
      title: "1. In-text citations",
      subsections: [
        {
          title: "Paraphrasing and in-text citations",
          example: "The point made by an analytic philosopher (O'Connor 1969, p. 32) is that values cannot be justified in this way. However Kneller (1963b, p. 102) insists that the theorist will inevitably be involved in value claims.",
          note: "Page, chapter or section numbers may be included in the in-text citation if the cited work is long and information helps the reader locate the relevant information. When the author's name is mentioned in-text (e.g. Kneller in the example above) add year and page numbers only to the in-text reference. Entries that have the same author and year are noted by adding a, b, c etc. to the year, both in-text e.g. Kneller (1963b, p. 102) and in the Reference List."
        },
        {
          title: "Direct quotes and in-text citations",
          examples: [
            "'Having a solid plan as part of research design is essential' (Hatch 2002, p. 46).",
            "Hatch (2002, p. 46) believes 'having a solid plan as part of research design is essential'."
          ],
          note: "Always include page numbers when citing a quotation and enclose the quote in single quotation marks."
        },
        {
          title: "Block quotes and in-text citations",
          example: "Inductive analysis is discussed:\n\nInductive thinking proceeds from the specific to the general. Understandings are generated by starting with specific elements and finding connections among them. To argue inductively is to begin with particular pieces of evidence, then pull them together into a meaningful whole. Inductive data analysis is a search for patterns of meaningful data so the general statements about phenomena under investigation can be made (Hatch 2002, p. 161).",
          note: "Place a quotation of 30 or more words in your work as a freestanding block. These quotes are usually indented e.g. 5 spaces and are in a smaller font e.g. 1 point size smaller than the surrounding text. Do not enclose the quote in quotation marks."
        }
      ]
    },
    {
      title: "2. Reference lists",
      content: "Arrange your list alphabetically by author.",
      examples: [
        "Cameron, B (Minister for Corrections, Victoria) 2007, Construction begins on high security unit, media release, Victoria, 28 March, viewed 16 April 2007, <http://www.dpc.vic.gov.au>.",
        "Douglas, N, Douglas, G & Derrett, R (eds) 2001, Special interest groups: context and cases, John Wiley & Sons, Brisbane, Qld.",
        "Farley, J 2008, 'The role of prices in conserving critical natural capital', Conservation Biology, vol. 22, no. 6, pp. 1399-1408, viewed 23 February 2009, <http://www3.interscience.wiley.com/cgi-bin/fulltext/121544162/PDFSTART>.",
        "Hatch, JA 2002, Doing qualitative research in education settings, State University of New York, Albany.",
        "Kneller, JP 1963a, Is logical thinking logical?, Ponsonby & Partridge, Dubbo.",
        "Kneller, JP 1963b, 'Thinking and logical interaction', Brain Logic, vol. 257, no. 4, pp. 54-62.",
        "O'Connor, DJ 1957, An introduction to the philosophy of education, Routledge & Kegan Paul, London.",
        "Peirson, G, Brown, R, Easton, S, Howard, P & Pinder, S 2006, Business finance, 9th edn, McGraw-Hill, North Ryde, NSW."
      ]
    },
    {
      title: "Common Reference Types",
      examples: [
        {
          type: "Book with one author",
          inText: "(O'Connor 1957, p. 33) or O'Connor (1957) states that...",
          reference: "O'Connor, DJ 1957, An introduction to the philosophy of education, Routledge & Kegan Paul, London."
        },
        {
          type: "Book with two authors",
          inText: "(Zikmund & D'Amico 1989) or Zikmund and D'Amico (1989) discussed....",
          reference: "Zikmund, WG & D'Amico, M 1989, Marketing, 3rd edn, Wiley, New York."
        },
        {
          type: "Book with more than three authors",
          inText: "(Peirson et al. 2006) or Peirson et al. (2006) argued against…",
          reference: "Peirson, G, Brown, R, Easton, S, Howard, P & Pinder, S 2006, Business finance, 9th edn, McGraw-Hill, North Ryde, NSW."
        },
        {
          type: "Journal Article",
          inText: "(Meindl 1987)",
          reference: "Meindl, D 1987, 'Chips for advanced computing', Scientific American, vol. 257, no. 4, pp.54-62."
        },
        {
          type: "Journal Article - Electronic format",
          inText: "(Farley 2008)",
          reference: "Farley, J 2008, 'The role of prices in conserving critical natural capital', Conservation Biology, vol. 22, no. 6, pp. 1399-1408, viewed 23 February 2009, <http://www3.interscience.wiley.com/cgi-bin/fulltext/121544162/PDFSTART>."
        },
        {
          type: "Web site",
          inText: "(Australian Bureau of Statistics 2010)",
          reference: "Australian Bureau of Statistics 2010, Australian Bureau of Statistics Belconnen, ACT, viewed 14 December 2010, <http://www.abs.gov.au/>."
        },
        {
          type: "Chapter in an edited book",
          inText: "(O'Regan 2002)",
          reference: "O'Regan, T 2002, 'A national cinema', in G Turner (ed.), The film cultures reader, Routledge, London, pp. 139-64."
        }
      ]
    },
    {
      title: "Important Notes",
      content: "REMEMBER: Your Faculty or School may require a different style so you should always check their requirements. This document is for general guidance only."
    }
  ]
};

export default harvardCitationGuide;






